import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { IParagraph } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { ICTTextParagraph } from '@tencent/slide-sdk-model';
import { TextParagraphPropsConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/text/text-paragraph/text-paragraph-props-converter';
import { TextParagraphCharacterConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/text/text-paragraph/text-paragraph-character-converter';
import { TextRunConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/text/text-run/text-run-converter';
import { TextRunStyleConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/text/text-run/text-run-style-converter';
import { isSimilarityEnv } from '@src/slide/infrastructure/module/independent/url-param-env';

/**
 * 文本段落属性
 */
export class TextParagraphConverter {
    /**
     * 文本段落属性的sdk -> ui 映射
     * @param sdkData
     */
    public static convertPartialDataFromSdkToUi(sdkData: DeepReadonly<DeepPartial<ICTTextParagraph>>): IParagraph {
        const paragraph: IParagraph = {};
        // 段落属性
        if (sdkData.pPr) {
            // 基本段落属性
            paragraph.paragraphProps = {};
            TextParagraphPropsConverter.convertPartialDataFromSdkToUi(sdkData.pPr, paragraph.paragraphProps);
            if (Object.keys(paragraph.paragraphProps).length === 0) {
                delete paragraph.paragraphProps;
            }

            // 项目符号
            paragraph.paragraphCharacter = {};
            TextParagraphCharacterConverter.convertPartialDataFromSdkToUi(sdkData.pPr, paragraph.paragraphCharacter);
            if (Object.keys(paragraph.paragraphCharacter).length === 0) {
                delete paragraph.paragraphCharacter;
            }
        }
        // 临时文字属性（占位符未输入时的占位文字 | 导入时可能带的文字结尾光标处的待输入文字）
        if (sdkData.endParaRPr) {
            paragraph.endParaRPr = { useEndParaStyle: true };
            TextRunStyleConverter.convertPartialDataFromSdkToUi(sdkData.endParaRPr, paragraph.endParaRPr);
        }
        // 文字（textRun）
        paragraph.textRuns = [];
        if (sdkData.egTextRun?.length) {
            sdkData.egTextRun.forEach((sdkTextRun) => {
                const uiTextRunArr = TextRunConverter.convertPartialDataFromSdkToUi(sdkTextRun);
                if (uiTextRunArr?.length) {
                    paragraph.textRuns = [...(paragraph.textRuns || []), ...uiTextRunArr];
                }
            });
        }

        /**
         * 特殊操作!!!!!，在相似度平台，模拟office每个段落后多一个空格的行为
         * slide本身并未实现此功能，所以只在相似度检测环境才执行此操作
         */
        if (isSimilarityEnv() && paragraph?.textRuns?.length) {
            const lastTextRuns = paragraph.textRuns[paragraph.textRuns.length - 1];
            if (lastTextRuns.text?.length) {
                lastTextRuns.text = `${lastTextRuns.text} `;
            }
        }

        return paragraph;
    }

    /**
     * 文本段落属性的ui-> sdk 映射
     * @param uiData
     */
    public static convertPartialDataFromUiToSdk(uiData: DeepReadonly<DeepPartial<IParagraph>>): ICTTextParagraph {
        const sdkParagraph: ICTTextParagraph = {};
        // 段落属性
        if (uiData.paragraphProps) {
            sdkParagraph.pPr = sdkParagraph.pPr || {};
            // 基本段落属性
            TextParagraphPropsConverter.convertPartialDataFromUiToSdk(uiData.paragraphProps, sdkParagraph.pPr);
        }
        if (uiData.paragraphCharacter) {
            sdkParagraph.pPr = sdkParagraph.pPr || {};
            // 项目符号
            TextParagraphCharacterConverter.convertPartialDataFromUiToSdk(uiData.paragraphCharacter, sdkParagraph.pPr);
        }
        // 文字（textRun）
        if (uiData.textRuns) {
            sdkParagraph.egTextRun = [];
            uiData.textRuns.forEach((uiTextRun) => {
                const sdkTextRunArr = TextRunConverter.convertPartialDataFromUiToSdk(uiTextRun);
                if (sdkTextRunArr?.length) {
                    sdkParagraph.egTextRun = [...(sdkParagraph.egTextRun || []), ...sdkTextRunArr];
                }
            });
        }
        // 临时文字属性（占位符未输入时的占位文字 | 导入时可能带的文字结尾光标处的待输入文字）
        if (uiData.endParaRPr) {
            sdkParagraph.endParaRPr = sdkParagraph.endParaRPr || {};
            TextRunStyleConverter.convertPartialDataFromUiToSdk(uiData.endParaRPr, sdkParagraph.endParaRPr);
        }
        // 临时文字属性（形状未输入时设置文字属性，将属性附加到endParaRPr上）
        const hasContentTextRun = uiData.textRuns?.filter(textRun => textRun?.text
            || (textRun?.type === 'runBr')
            || (textRun?.type === 'field'));
        if (hasContentTextRun?.length === 0 && sdkParagraph.egTextRun?.[0]) {
            // 有textRun，但是文本为空
            sdkParagraph.endParaRPr = sdkParagraph.egTextRun[0]?.r?.rPr;
        }
        return sdkParagraph;
    }
}
